'use client'

import { useState } from 'react'
import Link from 'next/link'

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState('')
  const [debugOtp, setDebugOtp] = useState<string | null>(null)
  const [step, setStep] = useState<'email' | 'otp'>('email')
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  function prettyError(code: string) {
    const [base, ...rest] = code.split(':')
    const baseCode = (base ?? '').trim()
    const detail = rest.join(':').trim()
    if (baseCode === 'OTP_EMAIL_NOT_CONFIGURED') return detail || 'OTP email is not configured. Please try again later.'
    if (baseCode === 'OTP_EMAIL_SEND_FAILED') return detail || 'Failed to send OTP email. Please try again.'
    if (baseCode === 'DB_NOT_CONFIGURED') return detail || 'Server is temporarily unavailable. Please try again later.'
    if (baseCode === 'RATE_LIMITED') return detail || 'Too many requests. Please wait and try again.'
    if (baseCode === 'AUTH_ERROR' || baseCode === 'AUTH_FAILED') return detail || 'Authentication failed. Please check the code and try again.'
    if (baseCode === 'UNEXPECTED_ERROR') return detail || 'Unexpected error. Please try again.'
    if (baseCode === 'CONFIG_ERROR') return detail || 'Server is temporarily unavailable. Please try again later.'
    return 'Request failed. Please try again.'
  }

  async function requestOtp() {
    setErr(null)
    setDebugOtp(null)
    setLoading(true)
    try {
      const res = await fetch('/api/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'request_otp', email }),
        credentials: 'include'
      })
      const json: any = await res.json().catch(() => null)
      if (!res.ok) {
        const code =
          (typeof json?.error?.code === 'string' && json.error.code) ||
          (typeof json?.error === 'string' && json.error) ||
          'REQUEST_FAILED'
        const serverMessage = typeof json?.error?.message === 'string' ? json.error.message : ''
        throw new Error(serverMessage ? `${code}: ${serverMessage}` : code)
      }
      const maybeOtp = typeof json?.data?.otp === 'string' ? json.data.otp : null
      setDebugOtp(maybeOtp)
      setStep('otp')
    } catch (e: any) {
      const code = typeof e?.message === 'string' ? e.message : 'REQUEST_FAILED'
      setErr(prettyError(code))
    } finally {
      setLoading(false)
    }
  }

  async function verifyOtp() {
    setErr(null)
    setLoading(true)
    try {
      const res = await fetch('/api/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'verify_otp', email, otp }),
        credentials: 'include'
      })
      const json: any = await res.json().catch(() => null)
      if (!res.ok) {
        const code =
          (typeof json?.error?.code === 'string' && json.error.code) ||
          (typeof json?.error === 'string' && json.error) ||
          'AUTH_FAILED'
        const serverMessage = typeof json?.error?.message === 'string' ? json.error.message : ''
        throw new Error(serverMessage ? `${code}: ${serverMessage}` : code)
      }
      window.location.href = '/dashboard'
    } catch (e: any) {
      const code = typeof e?.message === 'string' ? e.message : 'AUTH_FAILED'
      setErr(prettyError(code))
    } finally {
      setLoading(false)
    }
  }

  return (
    <main
      style={{
        minHeight: 'calc(100vh - 1px)',
        padding: '42px 18px',
        background:
          'radial-gradient(900px 520px at 10% 0%, rgba(99,102,241,0.18), transparent 60%), radial-gradient(900px 520px at 92% 0%, rgba(14,165,233,0.16), transparent 60%)'
      }}
    >
      <div style={{ maxWidth: 980, margin: '0 auto' }}>
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 14,
                border: '1px solid var(--border)',
                background: 'linear-gradient(135deg, #0ea5e9, #6366f1)',
                boxShadow: '0 14px 50px rgba(14,165,233,0.25)'
              }}
            />
            <div style={{ fontWeight: 1000, letterSpacing: '-0.03em', fontSize: 18 }}>DuoElevate</div>
          </div>
          <Link
            href="/"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '10px 14px',
              borderRadius: 12,
              border: '1px solid var(--border)',
              background: 'var(--panel)',
              color: 'var(--text)',
              textDecoration: 'none',
              fontWeight: 900
            }}
          >
            Home
          </Link>
        </header>

        <div style={{ marginTop: 18, display: 'grid', gridTemplateColumns: '1fr 420px', gap: 14, alignItems: 'start' }}>
          <section style={{ borderRadius: 22, border: '1px solid var(--border)', background: 'var(--panel)', padding: 18 }}>
            <div style={{ fontWeight: 1000, fontSize: 34, letterSpacing: '-0.04em', lineHeight: 1.05 }}>Sign in / Sign up</div>
            <p style={{ marginTop: 12, color: 'var(--muted)', lineHeight: 1.7, maxWidth: 520 }}>
              We use passwordless email verification. If you’re new, an account is created automatically.
            </p>

            <div style={{ marginTop: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center', padding: '8px 10px', borderRadius: 999, border: '1px solid var(--border)', background: 'var(--bg)', fontWeight: 900, fontSize: 12 }}>
                <span style={{ color: 'var(--muted)' }}>Step</span> {step === 'email' ? '1' : '2'} / 2
              </div>
              <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center', padding: '8px 10px', borderRadius: 999, border: '1px solid var(--border)', background: 'var(--bg)', fontWeight: 900, fontSize: 12 }}>
                <span style={{ color: 'var(--muted)' }}>Security</span> cookies + CSRF
              </div>
            </div>
          </section>

          <section style={{ borderRadius: 22, border: '1px solid var(--border)', background: 'var(--panel)', padding: 18 }}>
            <div style={{ fontWeight: 1000, fontSize: 16 }}>Continue</div>
            <div style={{ marginTop: 8, color: 'var(--muted)', lineHeight: 1.6 }}>
              {step === 'email' ? 'Enter your email to receive the code.' : 'Enter the 6-digit code sent to your email.'}
            </div>

            {err ? (
              <div style={{ marginTop: 12, padding: 12, borderRadius: 14, border: '1px solid #fecaca', background: '#fef2f2' }}>
                <div style={{ fontWeight: 1000, color: '#991b1b' }}>Error</div>
                <div style={{ marginTop: 6, color: '#991b1b' }}>{err}</div>
              </div>
            ) : null}

            <div style={{ marginTop: 14, display: 'grid', gap: 12 }}>
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontWeight: 900 }}>Email</span>
                <input
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  type="email"
                  autoComplete="email"
                  placeholder="you@domain.com"
                  style={{
                    padding: '12px 12px',
                    borderRadius: 14,
                    border: '1px solid var(--border)',
                    background: 'var(--bg)',
                    color: 'var(--text)',
                    fontWeight: 800
                  }}
                  disabled={loading || step !== 'email'}
                />
              </label>

              {step === 'otp' ? (
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontWeight: 900 }}>OTP</span>
                  <input
                    value={otp}
                    onChange={e => setOtp(e.target.value)}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="6-digit code"
                    style={{
                      padding: '12px 12px',
                      borderRadius: 14,
                      border: '1px solid var(--border)',
                      background: 'var(--bg)',
                      color: 'var(--text)',
                      fontWeight: 900,
                      letterSpacing: '0.2em'
                    }}
                    disabled={loading}
                  />
                </label>
              ) : null}

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {step === 'email' ? (
                  <button
                    onClick={requestOtp}
                    disabled={loading || !email.trim()}
                    style={{
                      padding: '12px 14px',
                      borderRadius: 14,
                      border: '1px solid #0b0b0f',
                      background: '#0b0b0f',
                      color: '#ffffff',
                      fontWeight: 1000,
                      cursor: loading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {loading ? 'Sending…' : 'Send OTP'}
                  </button>
                ) : (
                  <>
                    <button
                      onClick={verifyOtp}
                      disabled={loading || !otp.trim()}
                      style={{
                        padding: '12px 14px',
                        borderRadius: 14,
                        border: '1px solid #0b0b0f',
                        background: '#0b0b0f',
                        color: '#ffffff',
                        fontWeight: 1000,
                        cursor: loading ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {loading ? 'Verifying…' : 'Verify & Continue'}
                    </button>
                    <button
                      onClick={() => {
                        setOtp('')
                        setStep('email')
                      }}
                      disabled={loading}
                      style={{
                        padding: '12px 14px',
                        borderRadius: 14,
                        border: '1px solid var(--border)',
                        background: 'var(--panel)',
                        color: 'var(--text)',
                        fontWeight: 1000,
                        cursor: loading ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Change email
                    </button>
                  </>
                )}
              </div>

              {step === 'otp' && debugOtp ? (
                <div style={{ marginTop: 4, padding: 12, borderRadius: 14, border: '1px solid #bae6fd', background: '#f0f9ff' }}>
                  <div style={{ fontWeight: 1000, color: '#075985' }}>Debug OTP</div>
                  <div style={{ marginTop: 6, color: '#0c4a6e', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace', fontWeight: 900 }}>
                    {debugOtp}
                  </div>
                </div>
              ) : null}
            </div>
          </section>
        </div>

        <div style={{ marginTop: 14, color: 'var(--muted)', fontSize: 13 }}>
          Need to check deployment?{' '}
          <a href="/api/health" style={{ color: 'var(--text)', fontWeight: 900, textDecoration: 'none' }}>
            /api/health
          </a>
        </div>
      </div>

      <style jsx>{`
        @media (max-width: 980px) {
          div[style*='grid-template-columns: 1fr 420px'] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </main>
  )
}
